<template>
	<div class="blog-article-page">
		<HeadNav selectedLanguage="en" page="blog" />

		<main class="container">
			<nav class="breadcrumb" aria-label="Breadcrumb">
				<router-link to="/">Home</router-link>
				<span class="sep" aria-hidden="true">/</span>
				<router-link :to="listPath">{{ listLabel }}</router-link>
				<span class="sep" aria-hidden="true">/</span>
				<span class="current">{{ blog.h1 }}</span>
			</nav>

			<article class="article">
				<header class="article-header">
					<h1>{{ blog.h1 }}</h1>
					<div class="article-meta">
						<time :datetime="blog.date">{{ formatDate(blog.date) }}</time>
						<span class="meta-dot" aria-hidden="true" />
						<span>Secure VPN</span>
					</div>
				</header>

				<figure class="cover">
					<img :src="blog.poster" :alt="blog.h1" loading="eager">
				</figure>

				<template v-for="(block, index) in blog.blocks">
					<h2 v-if="block.type === 'h2'" :key="`h2-${index}`">{{ block.text }}</h2>
					<h3 v-else-if="block.type === 'h3'" :key="`h3-${index}`">{{ block.text }}</h3>
					<p
						v-else-if="block.type === 'p' && block.html"
						:key="`html-${index}`"
						v-html="block.html"
					/>
					<p v-else-if="block.type === 'p'" :key="`p-${index}`">{{ block.text }}</p>
					<ul v-else-if="block.type === 'ul'" :key="`ul-${index}`">
						<li v-for="(item, itemIndex) in block.items" :key="`ul-${index}-${itemIndex}`">
							{{ item }}
						</li>
					</ul>
					<ol v-else-if="block.type === 'ol'" :key="`ol-${index}`">
						<li v-for="(item, itemIndex) in block.items" :key="`ol-${index}-${itemIndex}`">
							{{ item }}
						</li>
					</ol>
					<pre
						v-else-if="block.type === 'code'"
						:key="`code-${index}`"
						class="code-block"
					><code>{{ block.text }}</code></pre>
					<div v-else-if="block.type === 'table'" :key="`table-${index}`" class="table-wrap">
						<table>
							<thead>
								<tr>
									<th v-for="header in block.headers" :key="header" scope="col">
										{{ header }}
									</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(row, rowIndex) in block.rows" :key="`row-${index}-${rowIndex}`">
									<td v-for="(cell, cellIndex) in row" :key="`cell-${index}-${rowIndex}-${cellIndex}`">
										{{ cell }}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</template>

				<section v-if="blog.faq && blog.faq.length" class="faq-section">
					<h2>Frequently Asked Questions</h2>
					<div class="faq-list">
						<div v-for="item in blog.faq" :key="item.question" class="faq-item">
							<h3>{{ item.question }}</h3>
							<p>{{ item.answer }}</p>
						</div>
					</div>
				</section>

				<footer class="article-footer">
					<router-link class="back-link" :to="listPath">
						<svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
							<path
								d="M16 10H4M9 5l-5 5 5 5"
								stroke="currentColor"
								stroke-width="1.8"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
						Back to {{ listLabel }}
					</router-link>
				</footer>
			</article>
		</main>
	</div>
</template>

<script>
import '@/css/base.scss'
import '@/css/vpn-theme.scss'
import HeadNav from '@/components/HeadNav.vue'
import vpnBlogs, { STREAMING_BLOG_SLUGS } from '@/documents/vpnBlogs'

export default {
	name: 'BlogArticle',
	components: {
		HeadNav,
	},
	computed: {
		blog() {
			const slug = this.$route.meta.blogSlug
			return vpnBlogs.find((item) => item.slug === slug) || vpnBlogs[0]
		},
		isStreamingBlog() {
			return STREAMING_BLOG_SLUGS.has(this.blog.slug)
		},
		listPath() {
			return this.isStreamingBlog ? '/blogs/' : '/guides/'
		},
		listLabel() {
			return this.isStreamingBlog ? 'Resources' : 'Guides'
		},
	},
	methods: {
		formatDate(date) {
			return new Date(date).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})
		},
	},
}
</script>

<style lang="scss" scoped>
.blog-article-page {
	min-height: 70vh;
	background:
		radial-gradient(ellipse 80% 45% at 8% -8%, rgba(91, 79, 232, 0.12), transparent 55%),
		radial-gradient(ellipse 50% 35% at 100% 0%, rgba(59, 105, 255, 0.08), transparent 50%),
		#f5f4fb;
	color: var(--vpn-ink);
	font-family: var(--vpn-font);
}

.container {
	width: var(--vpn-container);
	margin: 0 auto;
	padding: 28px 0 88px;
}

.breadcrumb {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	margin-bottom: 22px;
	font-size: 13px;
	font-weight: 600;
	color: var(--vpn-muted);
	animation: vpn-fade 0.5s ease both;
}

.breadcrumb a {
	color: var(--vpn-teal-deep);
	text-decoration: none;
}

.breadcrumb a:hover {
	color: var(--vpn-teal);
}

.sep {
	color: rgba(107, 127, 146, 0.55);
}

.current {
	color: var(--vpn-ink-soft);
}

.article {
	margin: 0 auto;
	padding: 40px 44px 36px;
	background: #fff;
	border: 1px solid var(--vpn-line);
	border-radius: 24px;
	box-shadow: var(--vpn-shadow-soft);
	animation: vpn-rise 0.65s ease both;
}

.article-header {
	margin-bottom: 28px;
	text-align: center;
}

h1 {
	margin: 0 0 16px;
	font-size: clamp(30px, 4.4vw, 42px);
	line-height: 1.15;
	letter-spacing: -0.03em;
	font-weight: 800;
}

.article-meta {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	font-size: 13px;
	font-weight: 700;
	color: var(--vpn-muted);
}

.meta-dot {
	width: 4px;
	height: 4px;
	border-radius: 50%;
	background: rgba(107, 127, 146, 0.5);
}

.cover {
	margin: 0 0 28px;
	overflow: hidden;
	border-radius: 18px;
	background: linear-gradient(135deg, #12122e, #2a2a6a);
	box-shadow: 0 16px 36px rgba(18, 18, 46, 0.18);
}

.cover img {
	display: block;
	width: 100%;
	aspect-ratio: 16 / 9;
	object-fit: cover;
}

h2 {
	margin: 30px 0 14px;
	font-size: clamp(22px, 2.6vw, 26px);
	line-height: 1.28;
	font-weight: 800;
	letter-spacing: -0.02em;
	color: var(--vpn-ink);
}

h3 {
	margin: 22px 0 10px;
	font-size: 18px;
	line-height: 1.4;
	font-weight: 800;
	color: var(--vpn-ink);
}

p,
li {
	font-size: 16px;
	line-height: 1.8;
	color: var(--vpn-ink-soft);
}

p {
	margin: 0 0 14px;
}

.code-block {
	margin: 8px 0 18px;
	padding: 16px 18px;
	overflow-x: auto;
	border: 1px solid #1e293b;
	border-radius: 12px;
	background: #0f172a;
	color: #f8fafc;
	font-size: 15px;
	line-height: 1.7;
}

.code-block code {
	display: block;
	margin: 0;
	padding: 0 !important;
	border-radius: 0;
	background: transparent !important;
	color: #f8fafc !important;
	font-family: Consolas, 'Courier New', monospace;
}

.article ::v-deep a {
	color: var(--vpn-teal-deep);
	font-weight: 700;
	text-decoration: none;
}

.article ::v-deep a:hover {
	color: var(--vpn-teal);
}

ul,
ol {
	margin: 0 0 18px;
	padding-left: 24px;
}

li {
	margin-bottom: 7px;
}

li:last-child {
	margin-bottom: 0;
}

.table-wrap {
	margin: 8px 0 22px;
	overflow-x: auto;
	border: 1px solid var(--vpn-line);
	border-radius: 16px;
}

table {
	width: 100%;
	min-width: 640px;
	border-collapse: collapse;
}

th,
td {
	padding: 14px 16px;
	border-bottom: 1px solid rgba(15, 48, 74, 0.06);
	text-align: left;
	font-size: 15px;
	line-height: 1.55;
}

th {
	background: linear-gradient(135deg, rgba(91, 79, 232, 0.1), rgba(59, 105, 255, 0.06));
	color: var(--vpn-ink);
	font-weight: 800;
}

td {
	color: var(--vpn-ink-soft);
}

tr:last-child td {
	border-bottom: 0;
}

.faq-section {
	margin-top: 38px;
	padding-top: 30px;
	border-top: 1px solid rgba(15, 48, 74, 0.06);
}

.faq-section > h2 {
	margin-top: 0;
}

.faq-list {
	display: grid;
	gap: 12px;
}

.faq-item {
	padding: 18px 20px;
	border: 1px solid var(--vpn-line);
	border-radius: 16px;
	background: linear-gradient(180deg, #fff, #faf9ff);
}

.faq-item h3 {
	margin: 0 0 8px;
	font-size: 17px;
}

.faq-item p {
	margin: 0;
}

.article-footer {
	margin-top: 32px;
	padding-top: 22px;
	border-top: 1px solid rgba(15, 48, 74, 0.06);
}

.back-link {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
	font-weight: 800;
	color: var(--vpn-teal-deep);
	text-decoration: none;
	transition: color 0.2s ease, gap 0.2s ease;
}

.back-link svg {
	width: 16px;
	height: 16px;
}

.back-link:hover {
	color: var(--vpn-teal);
}

@media (max-width: 720px) {
	.container {
		padding: 20px 0 64px;
	}

	.article {
		padding: 26px 18px 24px;
		border-radius: 18px;
	}

	.article-header {
		margin-bottom: 22px;
	}

	.faq-item {
		padding: 16px;
	}
}
</style>
